function round2(value) {
  return Number(Number(value || 0).toFixed(2));
}

function safePercent(value) {
  return Math.max(0, Math.min(100, Number(value || 0)));
}

export function billingPeriodOptions(policy = {}) {
  const options = Array.isArray(policy?.periodOptions) ? policy.periodOptions : [];
  const normalized = options
    .map((option) => ({
      months: Number(option?.months || 1),
      discountPercent: safePercent(option?.discountPercent),
      code: String(option?.code || ""),
    }))
    .filter((option) => [1, 3, 6, 12].includes(option.months));

  return normalized.length
    ? normalized
    : [
        { months: 1, discountPercent: 0, code: "period_1" },
        { months: 3, discountPercent: 0, code: "period_3" },
        { months: 6, discountPercent: 0, code: "period_6" },
        { months: 12, discountPercent: 0, code: "period_12" },
      ];
}

export function normalizeBillingPeriodMonths(value, policy = {}) {
  const months = Number(value || 1);
  return billingPeriodOptions(policy).some((option) => option.months === months)
    ? months
    : 1;
}

export function quoteMonthlySelection(services = [], policy = {}) {
  const selected = Array.isArray(services) ? services : [];
  const subtotal = round2(
    selected.reduce((sum, service) => sum + Number(service?.monthlyPrice || 0), 0),
  );
  const eligibleCount = selected.filter(
    (service) =>
      service?.discountEligible !== false &&
      service?.pricingRole !== "core" &&
      Number(service?.monthlyPrice || 0) > 0,
  ).length;
  const tiers = (Number(policy?.ruleVersion || 1) < 2 && Array.isArray(policy?.bundleDiscountTiers)
    ? policy.bundleDiscountTiers
    : [])
    .map((tier) => ({
      minPaidCapabilities: Number(tier?.minPaidCapabilities || 0),
      percent: safePercent(tier?.percent),
      code: String(tier?.code || ""),
    }))
    .sort((a, b) => a.minPaidCapabilities - b.minPaidCapabilities);
  const tier = [...tiers]
    .reverse()
    .find((candidate) => eligibleCount >= candidate.minPaidCapabilities);
  const percent = tier?.percent || 0;
  const discountAmount = round2(subtotal * (percent / 100));
  const total = round2(Math.max(0, subtotal - discountAmount));

  return {
    ruleVersion: Number(policy?.ruleVersion || 1),
    subtotal,
    total,
    savings: discountAmount,
    bundleDiscount: {
      code: tier?.code || "bundle_none",
      percent,
      amount: discountAmount,
      eligibleCapabilityCount: eligibleCount,
    },
  };
}

export function quotePeriodSelection(monthlyPricing = {}, periodMonths = 1, policy = {}) {
  const months = normalizeBillingPeriodMonths(periodMonths, policy);
  const option = billingPeriodOptions(policy).find((candidate) => candidate.months === months)
    || { months: 1, discountPercent: 0, code: "period_1" };
  const monthlyTotal = round2(monthlyPricing?.total || 0);
  const baseAmount = round2(monthlyTotal * months);
  const discountAmount = round2(baseAmount * (option.discountPercent / 100));
  const total = round2(Math.max(0, baseAmount - discountAmount));
  const catalogPeriodAmount = round2(Number(monthlyPricing?.subtotal || monthlyTotal) * months);

  return {
    months,
    code: option.code,
    discountPercent: option.discountPercent,
    baseAmount,
    discountAmount,
    total,
    equivalentMonthly: months ? round2(total / months) : total,
    totalSavings: round2(Math.max(0, catalogPeriodAmount - total)),
    catalogPeriodAmount,
  };
}
